import { IAkbarFlightDetails } from "libs/data-access/types";
import moment from "moment";

/**
 * Normalized flight segment interface for common UI rendering
 * This interface provides a unified structure for displaying flight segments
 * regardless of the provider (1G, Navitaire, or Akbar)
 */
export interface INormalizedFlightSegment {
  // core segment information
  origin: string;
  destination: string;
  departureTime: string;
  arrivalTime: string;
  // terminal information
  originTerminal: string | null;
  destinationTerminal: string | null;
  // flight details
  carrier: string;
  flightNumber: string;
  cabin: string;
  // calculated/derived information
  duration: string;
  baggage: string;
  // optional provider-specific details
  classOfService?: string; // 1G class of service
  fareClass?: string; // Akbar fare class
  aircraft?: string; // Aircraft type (Akbar)
  operatedBy?: {
    carrier: string | null;
    name: string | null;
  };
  // reference to original segment for provider-specific operations
  originalSegment: IAkbarFlightDetails;
}

interface IParsedJourneyKeySegment {
  carrier: string;
  flightNumber: string ;
  origin: string ;
  departureDateTime: string;
  departureDate: string ;
  departureTime: string ;
  originTerminal:string ;

  destination: string ;
  arrivalDateTime: string ;
  arrivalDate: string ;
  arrivalTime: string ;
  destinationTerminal:string;
 
  duration: string ;
  layoverDuration:string | null;
  layoverStation:string;

}

export function getJourneyKeySegments(
  journeyKey?: string
): IParsedJourneyKeySegment[] {
  if (!journeyKey) return [];

  const segments = journeyKey
    .split("~")
    .map(segment => segment.trim())
    .filter(Boolean);

  return segments.map((segment, index) => {
    const values = segment.split(",");

    const departure = values[4]?.trim() || "";
    const arrival = values[5]?.trim() || "";

    const nextValues =
      index < segments.length - 1
        ? segments[index + 1].split(",")
        : null;

    let layoverDuration: string | null = null;

    if (nextValues) {
      const nextDeparture = nextValues[4]?.trim();
      
      if (arrival && nextDeparture) {
        const diff = moment(nextDeparture).diff(moment(arrival), "minutes");
        const hours = Math.floor(diff / 60);
        const minutes = diff % 60;
        layoverDuration = `${hours}h ${minutes}m`;
      }
    }

    return {
      carrier: values[0]?.trim(),
      flightNumber: values[1]?.trim() ,
      origin: values[2]?.trim() ,
      destination: values[3]?.trim(),

      departureDateTime: departure ,
      arrivalDateTime: arrival,

      departureDate: departure ?? 'N/A',
      departureTime: departure ?? 'N/A',

      arrivalDate: arrival ?? 'N/A',
      arrivalTime: arrival ?? 'N/A',

      originTerminal: values[6]?.trim(),
      destinationTerminal: values[7]?.trim(),

      duration:getDuration1G(departure,arrival) ,
      layoverDuration,
      layoverStation:values[3]?.trim(),
    };
  });
}

/**
 * Normalizes an Akbar flight into a common segment format
 *
 * @param akbarFlight - The Akbar flight details object
 * @param connectionIndex - Index of the connection (null for main segment, 0+ for connections)
 * @param isInternational - Whether this is an international flight
 * @returns Normalized flight segment
 */
export function normalizeAkbarSegment(
  akbarFlight: IAkbarFlightDetails,
  connectionIndex: number | null,
  isInternational: boolean,
): INormalizedFlightSegment {
  // For main/first segment (connectionIndex === null or 0)

  if (connectionIndex === null || connectionIndex === 0) {

    const firstConnection =
      akbarFlight.connections.length > 0 ? akbarFlight.connections[0] : null;
    const journeyKeySegments = getJourneyKeySegments(akbarFlight.journeyKey);
    const firstJourney = journeyKeySegments[0];
    // console.log("First segment:",firstJourney)
    return {
      origin: akbarFlight.from,
      destination: firstConnection ? firstConnection.Airport : akbarFlight.to,
      departureTime: firstJourney?.departureTime || akbarFlight.departureTime,
      arrivalTime: firstJourney?.arrivalTime || akbarFlight.arrivalTime,
      originTerminal: firstJourney?.originTerminal || akbarFlight.departureTerminal || null,
      destinationTerminal: (firstJourney?.destinationTerminal) || (firstConnection ? null : akbarFlight.arrivalTerminal) || null,
      carrier: akbarFlight.mac,
      flightNumber: akbarFlight.flightNo,
      cabin: akbarFlight.cabin,
      duration: firstJourney?.duration || akbarFlight.duration,
      // duration:moment.duration(firstJourney?.arrivalTime?.diff(firstJourney?.departureTime)),
      baggage: akbarFlight.inclusions.Baggage || "N/A",
      fareClass: akbarFlight.fareClass,
      aircraft: akbarFlight.aircraft,
      originalSegment: akbarFlight,
    };
  }

  // For connection segments (subsequent segments)
  const connection = akbarFlight.connections[connectionIndex - 1];
  const nextConnection =
    connectionIndex < akbarFlight.connections.length
      ? akbarFlight.connections[connectionIndex]
      : null;
  const journeyKeySegments = getJourneyKeySegments(akbarFlight.journeyKey);
  const journeySegment =
    journeyKeySegments[connectionIndex] ||
    journeyKeySegments[journeyKeySegments.length - 1];
    // console.log("Journey segment:",journeySegment)
  return {
    origin: connection.Airport,
    destination: nextConnection ? nextConnection.Airport : akbarFlight.to,
    departureTime:
      journeySegment?.departureTime || connection.From || akbarFlight.departureTime,
    arrivalTime:
      journeySegment?.arrivalTime ||
      (nextConnection ? nextConnection.From || akbarFlight.arrivalTime : akbarFlight.arrivalTime),
    originTerminal: journeySegment?.originTerminal || null,
    destinationTerminal: journeySegment?.destinationTerminal || null,
    carrier: connection.MAC?.split("|")[0],
    flightNumber: connection.FlightNo,
    cabin: akbarFlight.cabin,
    duration: journeySegment?.duration || akbarFlight.duration,
    baggage: akbarFlight.inclusions.Baggage || "N/A",
    fareClass: akbarFlight.fareClass,
    aircraft: akbarFlight.aircraft,
    originalSegment: akbarFlight,
  };
}

/**
 * Normalizes all segments from an Akbar flight journey
 *
 * @param akbarFlight - The Akbar flight details object
 * @param isInternational - Whether this is an international flight
 * @returns Array of normalized flight segments
 */
export function normalizeAkbarJourney(
  akbarFlight: IAkbarFlightDetails,
  isInternational: boolean = false,
): INormalizedFlightSegment[] {
  // console.log("Akbar Flight:", akbarFlight);
  const segments: INormalizedFlightSegment[] = [];

  // Add the main segment
  segments.push(normalizeAkbarSegment(akbarFlight, null, isInternational));
  
  // Add connection segments if they exist
  if (akbarFlight.connections && akbarFlight.connections.length > 0) {
    // Filter only actual connections (Type === 'C'), not hops (Type === 'H')
    const actualConnections = akbarFlight.connections.filter(
      (conn) => conn.Type === "C",
    );

    for (let i = 1; i <= actualConnections.length; i++) {
      segments.push(normalizeAkbarSegment(akbarFlight, i, isInternational));
    }
  }
  return segments;
}

export function getTimeDifference(startTime: string, endTime: string): string {
  const start = moment(startTime, "HH:mm");
  const end = moment(endTime, "HH:mm");
  const diff = moment.duration(end.diff(start));
  // console.log("start, end and duration:",start,end,diff)
  return `${diff.hours()}h ${diff.minutes()}m`;
}

export function getDuration1G(startTime: string, endTime: string) {
  const start = moment.parseZone(startTime);
  const end = moment.parseZone(endTime);
  const duration = moment.duration(end.diff(start));
  return `${Math.abs(Math.floor(duration.asHours()))}h ${String(
    Math.abs(duration.minutes()),
  ).padStart(2, "0")}m`;
}

export function getDuration(
  startTime: string,
  arrvLTV: number,
  endTime: string,
  deptLTV: number,
) {
  const start = moment.parseZone(startTime);
  const end = moment.parseZone(endTime);

  // Difference in local-time offsets
  const timezoneDiff = arrvLTV - deptLTV;

  // Adjust arrival by timezone difference
  const duration = moment.duration(
    end.diff(start) - timezoneDiff * 60 * 1000,
  );

  const totalMinutes = Math.abs(Math.floor(duration.asMinutes()));

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

